import { createFileRoute, Link } from "@tanstack/react-router";
import { RelatedClusters } from "@/components/RelatedClusters";
import { ContentFreshness } from "@/components/ContentFreshness";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { APOSTILLE_COMPARE } from "@/data/apostille/compare";
import { APOSTILLE_STATUS } from "@/data/legal-facts";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const TITLE = "เทียบทางเลือกรับรองเอกสารเพื่อใช้ต่างประเทศ (Apostille vs นิติกรณ์)";
const DESC =
  "เลือกชั้นการรับรองให้ถูกตั้งแต่ครั้งแรก เทียบทีละข้อระหว่างตรา Apostille นิติกรณ์กรมการกงสุล ตราสถานทูตปลายทาง และการรับรองลายมือชื่อโดยทนายความ สอบถามทีมงานทาง LINE";

export const Route = createFileRoute("/apostille/compare/")({
  loader: () => {
    const url = absoluteUrl("/apostille/compare");
    return {
      h: {
        meta: [
          { title: ctrTitle(TITLE) },
          { name: "description", content: ctrDesc(DESC) },
          { property: "og:title", content: ctrTitle(TITLE) },
          { property: "og:description", content: ctrDesc(DESC) },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
          { name: "twitter:card", content: "summary" },
        ],
        links: [{ rel: "canonical", href: url }],
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: TITLE,
              itemListElement: APOSTILLE_COMPARE.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.h1,
                url: absoluteUrl(`/apostille/compare/${c.slug}`),
              })),
            }),
          },
        ],
      },
    };
  },
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: ApostilleCompareHub,
});

function ApostilleCompareHub() {
  return (
    <>
      <Breadcrumbs jsonLd
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "Apostille / นิติกรณ์", to: "/apostille" },
          { label: "เทียบทางเลือก" },
        ]}
      />
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-5">{TITLE}</h1>
        <p className="answer-summary text-lg mb-8 p-4 bg-muted rounded-xl" data-speakable>
          ปัจจุบันประเทศไทยยังไม่ออกตรา Apostille — อนุสัญญากรุงเฮกจะมีผลกับไทยวันที่{" "}
          {APOSTILLE_STATUS.inForceDateTh} (อ้างอิง hcch.net · mfa.go.th) ระหว่างนี้เอกสารไทยใช้เส้นทาง
          ต้นฉบับ → คำแปล → นิติกรณ์กรมการกงสุล → ตราสถานทูตปลายทางเมื่อปลายทางกำหนด
          หน้าด้านล่างเทียบทีละข้อว่าแต่ละชั้นทำหน้าที่อะไร และควรเรียงลำดับอย่างไร
        </p>

        <ul className="space-y-4 mb-10">
          {APOSTILLE_COMPARE.map((c) => (
            <li key={c.slug} className="border rounded-xl p-5">
              <h2 className="font-semibold mb-1">
                <Link
                  to="/apostille/compare/$slug"
                  params={{ slug: c.slug }}
                  className="text-primary hover:underline"
                >
                  {c.h1}
                </Link>
              </h2>
              <p className="text-sm text-muted-foreground">{c.description}</p>
            </li>
          ))}
        </ul>

        <div className="p-6 bg-muted rounded-xl mb-8">
          <p className="font-semibold">
            ส่งเช็กลิสต์ของหน่วยงานปลายทางมาให้ทีมเราตรวจก่อนเริ่มงาน โทร{" "}
            <a href="tel:0948958999" className="text-primary">094-895-8999</a> · LINE @Thainotary
          </p>
        </div>

        <p className="text-sm">
          <Link to="/apostille/questions" className="text-primary hover:underline">
            คำถามที่พบบ่อยเรื่องรับรองเอกสารเพื่อใช้ต่างประเทศ
          </Link>
          {" · "}
          <Link to="/apostille" className="text-primary hover:underline">
            กลับหน้าหลักบริการ
          </Link>
        </p>

        <p className="mt-8 text-xs text-muted-foreground">
          ค่าธรรมเนียมนิติกรณ์เป็นอัตราของกรมการกงสุล 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน
          (consular.mfa.go.th) แสดงแยกจากค่าบริการของสำนักงานเสมอ — ค่าบริการสอบถามทางโทรศัพท์ LINE หรืออีเมล
        </p>
        <RelatedClusters theme="apostille" exclude="apostille" />
      <ContentFreshness path="/apostille" />
    </section>
    </>
  );
}
